import React, { useState, useEffect } from 'react';
import PhysicalProgressChartDashboard from '../physicalProgressChart/PhysicalProgressChartDashboard';
import { X, Ship, Construction } from 'lucide-react';
import ReusableForm from '../common/ReusableForm';
import UpdateTaskPopup from '../master/taskTransaction/UpdateTaskPopup';
import { getRequest } from '../services/apiService';

const formFields = [
  { name: 'project', label: 'Project', type: 'select', required: true },
  { name: 'ship', label: 'Ship', type: 'select', required: false },
  { name: 'milestone', label: 'Milestone', type: 'select', required: true },
  { name: 'task', label: 'Task', type: 'select', required: true },
  { name: 'subtask', label: 'Subtask', type: 'select', required: false },
  { name: 'manpower', label: 'Manpower', type: 'number' },
  { name: 'budget', label: 'Budget', type: 'number' },
  { name: 'contribution_user', label: 'Contribution (%)', type: 'number' },
  { name: 'description', label: 'Description', type: 'textarea' },
  { name: 'file', label: 'File', type: 'file' },
  { name: 'taskType', label: 'Task Type', type: 'text' },
  { name: 'taskId', label: 'Task ID', type: 'text' },
  { name: 'unitId', label: 'Unit ID', type: 'text' },
  { name: 'subTaskId', label: 'Sub Task ID', type: 'text' },
  { name: 'month', label: 'Month', type: 'select', options: [
    { label: 'January', value: '01' },
    { label: 'February', value: '02' },
    { label: 'March', value: '03' },
    { label: 'April', value: '04' },
    { label: 'May', value: '05' },
    { label: 'June', value: '06' },
    { label: 'July', value: '07' },
    { label: 'August', value: '08' },
    { label: 'September', value: '09' },
    { label: 'October', value: '10' },
    { label: 'November', value: '11' },
    { label: 'December', value: '12' },
  ] },
  { name: 'year', label: 'Year', type: 'select', options: Array.from({length: 11}, (_, i) => ({ label: String(2020 + i), value: String(2020 + i) })) },
  { name: 'userPlanned', label: 'User Planned %', type: 'number' },
  { name: 'actualPercent', label: 'Actual %', type: 'number' },
  { name: 'actualStart', label: 'Actual Start', type: 'date' },
  { name: 'actualEnd', label: 'Actual End', type: 'date' },
  { name: 'start', label: 'Start', type: 'date' },
  { name: 'end', label: 'End', type: 'date' },
];

const WOTDashboard = () => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [showUpdatePopup, setShowUpdatePopup] = useState(false);
  const [formData, setFormData] = useState({});
  const [projects, setProjects] = useState([]);
  const [ships, setShips] = useState([]);
  const [selectedParentProject, setSelectedParentProject] = useState('1');
  const [selectedShipId, setSelectedShipId] = useState('12');

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const response = await getRequest('/master/projects/');
        if (response?.data) {
          setProjects(response.data);
          const defaultProject = response.data.find(p => p.id === 1);
          if (defaultProject) setSelectedParentProject(defaultProject.id.toString());
        }
      } catch (error) {
        console.error("Error fetching projects:", error);
      }
    };
    fetchProjects();
  }, []);

  useEffect(() => {
    const fetchShips = async () => {
      if (!selectedParentProject) return;
      try {
        const response = await getRequest(`/master/projects/?parent_id=${selectedParentProject}`);
        if (response?.data) {
          setShips(response.data);
          const defaultShip = response.data.find(s => s.id === 12);
          if (defaultShip) setSelectedShipId(defaultShip.id.toString());
        }
      } catch (error) {
        console.error("Error fetching ships:", error);
      }
    };
    fetchShips();
  }, [selectedParentProject]);

  const CustomSelect = ({ icon: Icon, label, value, onChange, options, className = "" }) => (
    <div className={`relative ${className}`}>
      <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-indigo-500">
        <Icon size={18} />
      </div>
      <select
        value={value}
        onChange={onChange}
        className="bg-white border border-gray-300 rounded-lg pl-10 pr-3 py-2.5 w-full text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 shadow-sm appearance-none"
        aria-label={label}
      >
        {options.map(option => (
          <option key={option.value} value={option.value}>{option.label}</option>
        ))}
      </select>
    </div>
  );

  // Dropdown state
  const [projectOptions, setProjectOptions] = useState([]);
  const [shipOptions, setShipOptions] = useState([]);
  const [milestoneOptions, setMilestoneOptions] = useState([]);
  const [taskOptions, setTaskOptions] = useState([]);
  const [subtaskOptions, setSubtaskOptions] = useState([]);

  // Fetch projects, milestones, tasks on mount
  useEffect(() => {
    // Fetch projects
    getRequest('/master/projects/').then(res => {
      const data = res?.data || res || [];
      setProjectOptions(data.map(p => ({ value: p.id, label: p.name })));
    }).catch(err => {
      console.error('Error fetching projects:', err);
      setProjectOptions([]);
    });

    // Fetch milestones - Fixed to use 'title' instead of 'name'
    getRequest('/master/milestones/').then(res => {
      const data = res?.data || res || [];
      setMilestoneOptions(data.map(m => ({ value: m.id, label: m.title })));
    }).catch(err => {
      console.error('Error fetching milestones:', err);
      setMilestoneOptions([]);
    });

    // Fetch tasks
    getRequest('/master/tasks/').then(res => {
      const data = res?.data || res || [];
      setTaskOptions(data.map(t => ({ value: t.id, label: t.name })));
    }).catch(err => {
      console.error('Error fetching tasks:', err);
      setTaskOptions([]);
    });
  }, []);

  // Fetch ships when project changes
  useEffect(() => {
    if (formData.project) {
      getRequest(`/master/projects/?parent_id=${formData.project}`).then(res => {
        const data = res?.data || res || [];
        setShipOptions(data.map(s => ({ value: s.id, label: s.name })));
      }).catch(err => {
        console.error('Error fetching ships:', err);
        setShipOptions([]);
      });
    } else {
      setShipOptions([]);
    }
  }, [formData.project]);

  // Fetch subtasks when task changes
  useEffect(() => {
    if (formData.task) {
      getRequest(`/master/tasks/?parent_task=${formData.task}`).then(res => {
        const data = res?.data || res || [];
        setSubtaskOptions(data.map(st => ({ value: st.id, label: st.name })));
      }).catch(err => {
        console.error('Error fetching subtasks:', err);
        setSubtaskOptions([]);
      });
    } else {
      setSubtaskOptions([]);
    }
  }, [formData.task]);

  const handleOpenForm = (mode) => {
    if (mode === 'add') {
      setShowAddForm(true);
    } else if (mode === 'update') {
      setShowUpdatePopup(true);
    }
  };

  const handleCloseAddForm = () => {
    setShowAddForm(false);
    setFormData({});
  };

  const handleCloseUpdatePopup = () => {
    setShowUpdatePopup(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
    handleCloseAddForm();
  };

  // Compose dynamic fields with options
  const dynamicFields = formFields.map(field => {
    if (field.name === 'project') return { ...field, options: projectOptions };
    if (field.name === 'ship') return { ...field, options: shipOptions };
    if (field.name === 'milestone') return { ...field, options: milestoneOptions };
    if (field.name === 'task') return { ...field, options: taskOptions };
    if (field.name === 'subtask') return { ...field, options: subtaskOptions };
    return field;
  });

  return (
    <div className="h-screen flex flex-col">
      <header className="sticky top-0 z-30 w-full mb-8 bg-white border rounded-2xl shadow-lg border-slate-100 px-4 sm:px-6 lg:px-8 py-4 sm:py-6
                   flex flex-col sm:flex-row flex-wrap items-start sm:items-center gap-4 sm:gap-6 mx-auto max-w-screen-6xl">
        {/* Title Section */}
        <div className="flex-shrink-0 mr-0 sm:mr-6"> 
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-indigo-900 tracking-tight">
            WOT Dashboard
          </h1>
        </div>

        {/* Dropdowns Section */}
        <div className="flex flex-col sm:flex-row flex-1 gap-3 sm:gap-4 md:gap-6 w-full sm:w-auto">
          <CustomSelect
            icon={Construction}
            label="Project"
            value={selectedParentProject}
            onChange={(e) => setSelectedParentProject(e.target.value)}
            options={projects.map(project => ({ value: project.id, label: project.name }))}
            className="w-full sm:w-[200px] lg:w-[240px]"
          />
          <CustomSelect
            icon={Ship}
            label="Ship"
            value={selectedShipId}
            onChange={(e) => setSelectedShipId(e.target.value)}
            options={ships.map(ship => ({ value: ship.id, label: ship.name }))}
            className="w-full sm:w-[200px] lg:w-[240px]"
          />
        </div>

        {/* Buttons Section */}
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 w-full sm:w-auto">
          <button
            className="px-4 py-2 sm:px-5 sm:py-2.5 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 shadow text-sm sm:text-base w-full sm:w-auto"
            onClick={() => handleOpenForm('update')}
          >
            Update
          </button>
          <button
            className="px-4 py-2 sm:px-5 sm:py-2.5 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 shadow text-sm sm:text-base w-full sm:w-auto"
            onClick={() => handleOpenForm('add')}
          >
            Add
          </button>
        </div>
      </header>

      <div className="mx-4 mb-6">
        <PhysicalProgressChartDashboard selectedShipId={selectedShipId} />
      </div>

      {showUpdatePopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-7xl animate-fade-in flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 bg-[#1e3a8a] text-white rounded-t-xl border-b border-gray-200 sticky top-0 z-10">
              <h3 className="text-xl font-bold tracking-wide">Update Task</h3>
              <button onClick={handleCloseUpdatePopup} className="text-white hover:text-gray-300">
                <X size={24} />
              </button>
            </div>
            <div className="p-4 flex-1 overflow-y-auto">
              <UpdateTaskPopup isOpen={showUpdatePopup} onClose={handleCloseUpdatePopup} />
            </div>
          </div>
        </div>
      )}

      {showAddForm && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-2 sm:p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-xs sm:max-w-sm md:max-w-2xl lg:max-w-4xl xl:max-w-6xl 2xl:max-w-7xl relative max-h-[95vh] sm:max-h-[90vh] overflow-hidden">
            <ReusableForm
              formData={formData}
              setFormData={setFormData}
              fields={dynamicFields}
              onSubmit={handleSubmit}
              onHide={handleCloseAddForm}
              submitLabel="Add"
              headerText="Add System"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default WOTDashboard;